import { startOfWeek, isBefore, endOfWeek, isAfter, format, addWeeks, getWeek, addDays, startOfDay, isSameDay, parseISO } from "date-fns";
import { es } from "date-fns/locale";

export const generateTimelineWeeksWithDateFns = (startDate: Date, endDate: Date): { start: Date, end: Date, label: string, monthLabel: string, weekNumber: number }[] => {
  const weeks: { start: Date, end: Date, label: string, monthLabel: string, weekNumber: number }[] = [];
  let current = startOfWeek(startDate, { weekStartsOn: 1 }); // Monday as start

  while (isBefore(current, endDate) || current.getTime() === endDate.getTime()) {
    const weekStart = current;
    let weekEnd = endOfWeek(current, { weekStartsOn: 1 });
    if (isAfter(weekEnd, endDate)) weekEnd = endDate;

    const weekNumber = getWeek(weekStart, { weekStartsOn: 1 });
    const monthLabel = format(weekStart, 'MMM', { locale: es });

    weeks.push({
      start: weekStart,
      end: weekEnd,
      label: `${format(weekStart, 'dd MMM', { locale: es })} - ${format(weekEnd, 'dd MMM', { locale: es })}`,
      monthLabel: monthLabel,
      weekNumber: weekNumber
    });

    current = addWeeks(current, 1);
  }

  return weeks;
}

export interface TimelineDay {
  date: Date;
  dayOfWeek: string;
  dayNumber: number;
  monthLabel: string;
  isWeekend: boolean;
  isToday: boolean;
}

export const generateTimelineDays = (startDate: Date, endDate: Date): TimelineDay[] => {
  const days: TimelineDay[] = [];
  let current = startOfDay(startDate);
  const today = startOfDay(new Date());

  while (isBefore(current, endDate) || isSameDay(current, endDate)) {
    const dayOfWeek = current.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    days.push({
      date: new Date(current),
      dayOfWeek: format(current, 'EEEEEE', { locale: es }), // Short day name (L, M, X, J, V, S, D)
      dayNumber: current.getDate(),
      monthLabel: format(current, 'MMM', { locale: es }),
      isWeekend: isWeekend,
      isToday: isSameDay(current, today)
    });

    current = addDays(current, 1);
  }

  return days;
}

export const formatDateToYYYYMMDD = (date: Date | string): string => {
  if (typeof date === 'string') {
    // Validate and normalize string input using parseISO to avoid timezone issues
    const parsed = parseISO(date);
    if (isNaN(parsed.getTime())) {
      throw new Error('Invalid date string provided');
    }
    return format(parsed, 'yyyy-MM-dd');
  }

  return format(date, 'yyyy-MM-dd');
}


export const getISOWeekNumber = (d: Date): number => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}